import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, AlertCircle, UserCheck, GraduationCap, Shield, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/login.scss';

const LoginForm: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Por favor completa todos los campos');
      return;
    }

    try {
      await login(username.trim(), password);
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message);
    }
  };

  // Quick login function for test users
  const quickLogin = async (testUser: string, testPassword: string) => {
    setError('');
    setUsername(testUser);
    setPassword(testPassword);

    try {
      await login(testUser, testPassword);
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message);
    }
  };

  // Test users credentials
  const testUsers = [
    {
      email: 'admin@mentoring.com',
      password: 'Admin123!',
      role: 'Super Admin',
      icon: Shield,
      iconClass: 'admin-icon'
    },
    {
      email: 'sofia.mentor@tech.com',
      password: 'Mentor123!',
      role: 'Mentor',
      icon: UserCheck,
      iconClass: 'mentor-icon'
    },
    {
      email: 'ana.mentee@student.com',
      password: 'Mentee123!',
      role: 'Mentee',
      icon: GraduationCap,
      iconClass: 'mentee-icon'
    }
  ];

  return (
    <div className="login-container flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="login-box">
        <div className="login-logo">
          <img className="login-logo-mitsui" src="/img/mitsui_logo_corporate.svg" alt="MITSUI &amp; CO." />
          <span className="login-logo-sep" aria-hidden />
          <img className="login-logo-cornerstone" src="/img/logo_cornerstone.svg" alt="Cornerstone" />
        </div>
        <div className="login-header">
          <p>Plataforma de Mentoría · Inicia sesión</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="error-message">
              <AlertCircle className="error-icon" />
              <span className="error-text">{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Usuario</label>
            <div className="input-wrapper">
              <User className="input-icon" />
              <input
                id="username"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="tu.usuario"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <div className="input-wrapper">
              <Lock className="input-icon" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Tu contraseña"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div className="form-options">
            <div className="remember-me">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
              />
              <label htmlFor="remember-me">
                Recordarme
              </label>
            </div>

            <button type="button" className="forgot-password" onClick={() => navigate('/forgot-password')}>
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="login-button"
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        {/* Quick Access for Test Users - DEVELOPMENT ONLY */}
        {process.env.NODE_ENV === 'development' && (
          <div className="test-users-section">
            <div className="test-users-title">
              <span>Acceso Rápido - Usuarios de Prueba</span>
            </div>
            <div className="test-users-grid">
              {testUsers.map((user) => {
                const Icon = user.icon;
                return (
                  <button
                    key={user.email}
                    onClick={() => quickLogin(user.email, user.password)}
                    disabled={loading}
                    className="test-user-button"
                  >
                    <div className="user-info">
                      <div className={`user-icon-wrapper ${user.iconClass}`}>
                        <Icon />
                      </div>
                      <div className="user-details">
                        <p className="user-role">{user.role}</p>
                        <p className="user-email">{user.email}</p>
                      </div>
                    </div>
                    <ChevronRight className="arrow-icon" />
                  </button>
                );
              })}
            </div>
            <div className="test-password-hint">
              <p>
                🔐 Contraseña: Admin123! | Mentor123! | Mentee123!
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;